import { find } from 'lodash-es';
import { InfoType } from '../App';
import { appEnv } from '../config/env';
import Authentication from './authentication';
import { ApiError, ApiResponse } from './api';
import { AppInitializer } from './app-initializer';
import Repository from './repository';
import { syncSessionMetadata } from './auth-token-storage';
import { UserType } from '../stores/authenticated';
import { SystemType } from '../stores/system';
import store from '../stores';

const authentication = new Authentication();

const BOOTSTRAP_RECOVERY_ATTEMPTS = 3;
const BOOTSTRAP_RECOVERY_DELAY_MS = 500;
const BOOTSTRAP_ONLINE_WAIT_MS = 30_000;

export type SessionRecoveryResult = 'recovered' | 'auth_failed' | 'transient_failed';

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function isTransientSessionError(err: unknown): boolean {
  if (err instanceof ApiError) {
    if (err.refreshFailureReason === 'transient') {
      return true;
    }
    if (err.response?.status === 429) {
      return true;
    }
  }
  if (err instanceof TypeError) {
    return true;
  }
  return false;
}

function waitForOnline(timeoutMs: number): Promise<boolean> {
  if (typeof window === 'undefined') {
    return Promise.resolve(false);
  }
  if (window.navigator.onLine) {
    return Promise.resolve(true);
  }
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => {
      window.removeEventListener('online', onOnline);
      resolve(false);
    }, timeoutMs);
    const onOnline = (): void => {
      window.clearTimeout(timer);
      window.removeEventListener('online', onOnline);
      resolve(true);
    };
    window.addEventListener('online', onOnline);
  });
}

async function fetchFallbackUser(userId: number): Promise<UserType | null> {
  try {
    const repository = new Repository();
    const { data } = await repository.getUser(userId);
    return data as UserType;
  } catch {
    return null;
  }
}

type VerifySessionResponse = {
  valid: boolean;
  user: UserType;
};

function resolveFallbackUser(bootUserId: number, fallbackUser?: UserType | null): UserType | null {
  if (fallbackUser?.id === bootUserId) {
    return fallbackUser;
  }
  return null;
}

function buildMinimalInfo(user: UserType): InfoType {
  return {
    talkings: [],
    meetings: [],
    departments: [],
    users: [user],
    privacy: [],
    totalUsers: 1,
    presentUsers: 0,
    absentUsers: 0,
    activeMeetings: 0,
    meetingsLimit: 0,
    usersLimit: 0,
  };
}

function ensureAccessTokenExpiresAt(): number {
  let latestExpires = store.getState().authenticated.accessTokenExpiresAt;
  if (
    latestExpires == null ||
    !Number.isFinite(latestExpires) ||
    !authentication.isSessionValid(latestExpires)
  ) {
    syncSessionMetadata(Date.now() + appEnv.accessTokenClientTtlFallbackMs);
    latestExpires = store.getState().authenticated.accessTokenExpiresAt as number;
  }
  return latestExpires as number;
}

function loginAuthenticatedUser(
  bootUserId: number,
  user: UserType,
  info: InfoType,
  system: SystemType,
  accessTokenExpiresAt: number,
): void {
  AppInitializer.loggedIn({
    accessTokenExpiresAt,
    userId: bootUserId,
    user,
    info,
    system,
  });
}

/** httpOnly セッションが有効なら Redux を復元する。 */
export async function bootAuthenticatedApp(
  bootUserId: number,
  fallbackUser?: UserType | null,
): Promise<boolean> {
  try {
    const response = await AppInitializer.dataLoad();
    const latestExpires = ensureAccessTokenExpiresAt();

    const info = response[0] as ApiResponse<InfoType>;
    const system = response[1] as ApiResponse<SystemType>;
    const user =
      (find(info.data.users as UserType[], { id: bootUserId }) as UserType | undefined) ??
      (await fetchFallbackUser(bootUserId)) ??
      resolveFallbackUser(bootUserId, fallbackUser);

    if (!user) {
      return false;
    }

    loginAuthenticatedUser(bootUserId, user, info.data, system.data, latestExpires);
    return true;
  } catch {
    const user = resolveFallbackUser(bootUserId, fallbackUser);
    if (!user) {
      return false;
    }
    try {
      const latestExpires = ensureAccessTokenExpiresAt();
      loginAuthenticatedUser(
        bootUserId,
        user,
        buildMinimalInfo(user),
        store.getState().system as SystemType,
        latestExpires,
      );
      return true;
    } catch {
      return false;
    }
  }
}

/** GET /auth/verify で現在ユーザーを特定し、bootstrap する（1 回分）。 */
export async function recoverSessionFromHttpOnlyCookies(): Promise<SessionRecoveryResult> {
  try {
    const repository = new Repository();
    const { data } = await repository.verifyToken();
    const payload = data as VerifySessionResponse;
    if (!payload.valid || payload.user?.id == null) {
      return 'auth_failed';
    }
    const booted = await bootAuthenticatedApp(payload.user.id as number, payload.user);
    return booted ? 'recovered' : 'auth_failed';
  } catch (err) {
    if (isTransientSessionError(err)) {
      return 'transient_failed';
    }
    if (err instanceof ApiError && err.response?.status === 401) {
      return 'auth_failed';
    }
    return 'transient_failed';
  }
}

/**
 * 起動時 bootstrap 用。transient 失敗時は短いリトライと online 待ちのあと再試行する。
 */
export async function recoverSessionFromHttpOnlyCookiesWithRetry(): Promise<boolean> {
  for (let attempt = 0; attempt < BOOTSTRAP_RECOVERY_ATTEMPTS; attempt += 1) {
    const result = await recoverSessionFromHttpOnlyCookies();
    if (result === 'recovered') {
      return true;
    }
    if (result === 'auth_failed') {
      return false;
    }
    if (attempt < BOOTSTRAP_RECOVERY_ATTEMPTS - 1) {
      await sleep(BOOTSTRAP_RECOVERY_DELAY_MS);
    }
  }

  const cameOnline = await waitForOnline(BOOTSTRAP_ONLINE_WAIT_MS);
  if (cameOnline) {
    const afterOnline = await recoverSessionFromHttpOnlyCookies();
    return afterOnline === 'recovered';
  }

  return false;
}

/** 認証済みセッションが Redux に復元済みか */
export function isAuthenticatedSessionRestored(): boolean {
  const auth = store.getState().authenticated;
  return (
    auth.user != null &&
    auth.userId != null &&
    authentication.isSessionValid(auth.accessTokenExpiresAt)
  );
}
